import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, Shield, Clock, Users2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WaitlistDialog from "../components/WaitlistDialog";
import TrialForm from "../components/TrialForm";
import ROIStrip from "../components/ROIStrip";
import FAQ from "../components/FAQ";

export default function TrialLandingPage() {
    const [waitlistOpen, setWaitlistOpen] = useState(false);
    const formRef = useRef(null);

    useEffect(() => {
        document.title = "Prøv Cleany gratis i 14 dage · Cleany";
        // Landing page for paid ads — noindex
        const robots =
            document.querySelector("meta[name='robots']") ||
            (() => {
                const m = document.createElement("meta");
                m.name = "robots";
                document.head.appendChild(m);
                return m;
            })();
        robots.setAttribute("content", "noindex, nofollow");
        return () => robots.setAttribute("content", "index, follow");
    }, []);

    const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

    return (
        <div className="min-h-screen bg-white" data-testid="trial-landing-page">
            <Navbar onCtaClick={() => setWaitlistOpen(true)} />

            <main className="pt-24 pb-24">
                {/* ========================================================== */}
                {/* HERO + FORM SIDE-BY-SIDE                                    */}
                {/* ========================================================== */}
                <section className="relative pt-14 pb-16 overflow-hidden">
                    <div className="absolute inset-0 -z-10 bg-gradient-to-b from-sky-50/70 via-white to-white" />
                    <div className="absolute -top-16 right-1/4 -z-10 h-72 w-72 rounded-full bg-sky-200/30 blur-3xl" />

                    <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
                        {/* Copy */}
                        <div className="lg:col-span-6">
                            <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50/70 px-3 py-1 text-xs font-semibold text-sky-700">
                                <Sparkles className="h-3.5 w-3.5" />
                                14 dage gratis · Ingen binding
                            </div>
                            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
                                Prøv Cleany
                                <span className="block text-sky-600">gratis i 14 dage.</span>
                            </h1>
                            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
                                Vi opretter jeres konto, flytter jeres eksisterende lister ind og viser jer hvordan I bruger systemet i praksis — inden I overhovedet træffer et valg.
                            </p>
                            <ul className="mt-8 space-y-3">
                                {[
                                    "Vi opretter kontoen og flytter jeres data ind for jer",
                                    "I bruger de 14 dage på systemet — ikke på opsætning",
                                    "Ingen kreditkort. Ingen binding. Ingen betingelser.",
                                    "Dansk support pr. telefon og chat",
                                ].map((t, i) => (
                                    <li key={i} className="flex gap-3 text-sm text-slate-700">
                                        <div className="flex-shrink-0 h-5 w-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mt-0.5">
                                            <Check className="h-3 w-3" />
                                        </div>
                                        <span>{t}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-8 hidden lg:flex items-center gap-6">
                                {[
                                    { icon: Clock, label: "Under 1 uge til I kører live" },
                                    { icon: Shield, label: "Data hostet i EU/EØS" },
                                    { icon: Users2, label: "5-100+ medarbejdere" },
                                ].map((s, i) => (
                                    <div key={i} className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                                        <s.icon className="h-4 w-4 text-slate-400" />
                                        {s.label}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Form card */}
                        <div ref={formRef} className="lg:col-span-6 scroll-mt-24">
                            <div className="rounded-2xl bg-white p-7 md:p-9 shadow-2xl shadow-sky-900/10 border-2 border-sky-200 ring-1 ring-sky-100">
                                <div className="mb-5">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-sky-700">Kom i gang</div>
                                    <h2 className="mt-1 font-display text-2xl font-bold text-slate-900">
                                        Start jeres 14-dages prøve
                                    </h2>
                                    <p className="mt-1 text-sm text-slate-500">
                                        I hører fra os inden for én arbejdsdag.
                                    </p>
                                </div>
                                <TrialForm source="14-dages-proeve" />
                            </div>
                        </div>
                    </div>
                </section>

                {/* ========================================================== */}
                {/* ROI-STRIP                                                   */}
                {/* ========================================================== */}
                <ROIStrip onCtaClick={scrollToForm} />

                {/* Secondary CTA between ROI and FAQ */}
                <section className="py-14 md:py-16 bg-white">
                    <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
                        <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight">
                            Klar til at prøve det på jeres egne data?
                        </h2>
                        <p className="mt-3 text-slate-600 leading-relaxed">
                            Vi tager kontakt inden for én arbejdsdag, og har jeres konto klar samme uge.
                        </p>
                        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                type="button"
                                onClick={scrollToForm}
                                data-testid="mid-cta-trial"
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold px-6 h-12 shadow-lg shadow-sky-600/20 transition-all active:scale-[0.97]"
                            >
                                Start gratis prøve
                                <ArrowRight className="h-4 w-4" />
                            </button>
                            <Link
                                to="/beregn-pris"
                                data-testid="mid-cta-calc"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white hover:border-slate-400 text-slate-800 font-semibold px-6 h-12 transition-colors"
                            >
                                Beregn pris først
                            </Link>
                        </div>
                    </div>
                </section>

                {/* ========================================================== */}
                {/* FAQ                                                         */}
                {/* ========================================================== */}
                <FAQ />
            </main>

            <Footer />
            <WaitlistDialog open={waitlistOpen} onClose={() => setWaitlistOpen(false)} source="14-dages-proeve" />
        </div>
    );
}
